// @flow

import * as React from 'react';
import _ from 'lodash';

/** @jsx jsx */
import { jsx } from '@emotion/core';

import { getMenuItemCss } from './Styles';

import type { ComponentType } from 'react';

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
export type Item = *;

type ListItemProps = {
  onClick: (SyntheticMouseEvent<HTMLElement>) => void,
};

type MenuItemProps = {
  getLabelForItem: (item: Item) => string,
  highlightedItemRef: React.Ref<*>,
  index: number,
  isHighlighted: boolean,
  isDisabled: boolean,
  item: Item,
  liProps: ListItemProps,
  MenuItemComponent: ComponentType<*>,
};

/** @component */
export const MenuItem = (props: MenuItemProps) => {
  const {
    getLabelForItem,
    highlightedItemRef,
    isDisabled,
    isHighlighted,
    item,
    liProps,
    MenuItemComponent,
  } = props;
  return (
    // Downshift already handles a11y for us
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions,jsx-a11y/click-events-have-key-events */
    <li
      {...liProps}
      css={getMenuItemCss({
        isHighlighted,
        isDisabled,
      })}
      onClick={e => {
        // This stops the browser from double registering this event in some cases
        e.preventDefault();
        e.stopPropagation();

        if (liProps.onClick) {
          liProps.onClick(e);
        }
      }}
      ref={isHighlighted ? highlightedItemRef : null}
      role="menuitem"
    >
      <MenuItemComponent getLabelForItem={getLabelForItem} item={item} />
    </li>
  );
};

const arePropsEqual = (prevProps, nextProps) =>
  _.isEqual(_.omit(prevProps, 'liProps'), _.omit(nextProps, 'liProps'));

export default React.memo(MenuItem, arePropsEqual);
